/**
 * Editable-content schema for the report builder.
 *
 * One entry per editable text block in a report (headings, narrative paragraphs
 * and the auto-generated data sentences). This drives the report Settings UI
 * (which fields to show, their labels, placeholders and available tokens) and is
 * the single source of truth for the override keys the section components read
 * via `useReportContentOverride`.
 *
 * `placeholder` is the built-in default copy in plain-text, tokenised form. It is
 * shown greyed in the input as a hint; it is NOT persisted and is NOT the value
 * rendered when a block is left blank (the component keeps its own default JSX).
 */

export type ReportContentBlockKind = 'heading' | 'body' | 'dataSentence'

export interface ReportContentBlock {
  /** Stable override key, e.g. `methodology.intro1`. */
  key: string
  /** Field label in the settings UI. */
  label: string
  kind: ReportContentBlockKind
  /** Plain-text, tokenised rendering of the built-in default (UI hint only). */
  placeholder: string
  /** Tokens usable in this block, e.g. `['company','year']`. */
  tokens: string[]
  /**
   * When set, this block reads/writes a top-level settings field directly
   * (e.g. `customTitle`) instead of the `sectionContent` override map. Used for
   * cover title/year, which have their own columns.
   */
  settingsField?: string
}

export interface ReportContentArea {
  /**
   * The `excludedSections` key that toggles this area's visibility, or `null`
   * for areas that are always shown (e.g. the cover page).
   */
  sectionKey: string | null
  /** Area label in the settings UI. */
  label: string
  /** Optional note shown under the area label (e.g. shared-toggle behaviour). */
  note?: string
  blocks: ReportContentBlock[]
}

/** Tokens available report-wide (merged into every block's local tokens). */
export const CARBON_GLOBAL_TOKENS = ['company', 'year', 'calendarYear', 'partner', 'organisation'] as const
export const PROJECT_GLOBAL_TOKENS = ['company', 'projectLabel', 'organisation'] as const

const METHODOLOGY_SCOPE_TOKENS = ['organisation']

// ---------------------------------------------------------------------------
// Carbon Summary Report
// ---------------------------------------------------------------------------

export const CARBON_SUMMARY_CONTENT: ReportContentArea[] = [
  {
    sectionKey: null,
    label: 'Cover page',
    note: 'The cover image is set on the Design tab. Set the cover title, year and an optional strapline here.',
    blocks: [
      { key: 'cover.title', label: 'Cover page title', kind: 'heading', placeholder: 'Greenhouse Gas Emissions Report', tokens: [], settingsField: 'customTitle' },
      { key: 'cover.year', label: 'Cover page year', kind: 'heading', placeholder: 'Defaults to the report period name', tokens: [], settingsField: 'customReportPeriodName' },
      {
        key: 'cover.strapline',
        label: 'Strapline (optional)',
        kind: 'heading',
        placeholder: 'e.g. Prepared for the board and external stakeholders',
        tokens: ['company', 'year', 'calendarYear'],
      },
    ],
  },
  {
    sectionKey: 'executiveSummary',
    label: 'Executive Summary',
    blocks: [
      { key: 'executiveSummary.heading', label: 'Heading', kind: 'heading', placeholder: 'Executive Summary', tokens: [] },
      {
        key: 'executiveSummary.body1',
        label: 'Paragraph 1',
        kind: 'body',
        placeholder:
          "This Greenhouse Gas Emissions (GHG) Report provides a comprehensive analysis of {{company}}'s carbon footprint for the {{year}} {{calendarYear}}, covering Scope 1, Scope 2 and Scope 3 emissions. The report is aligned with the GHG Protocol using the data from the {{partner}} Carbon Accounting emissions assessment.",
        tokens: ['company', 'year', 'calendarYear', 'partner'],
      },
      {
        key: 'executiveSummary.body2',
        label: 'Paragraph 2',
        kind: 'body',
        placeholder:
          'The primary objective of this report is to quantify and analyse the GHG emissions across all operational boundaries, identify key emission sources, and assess progress towards our sustainability goals. The report covers emissions from all facilities, operations, and supply chain activities within our global footprint, providing a clear picture of our environmental impact.',
        tokens: ['company'],
      },
    ],
  },
  {
    sectionKey: 'methodology',
    label: 'Methodology',
    blocks: [
      { key: 'methodology.heading', label: 'Heading', kind: 'heading', placeholder: 'Methodology', tokens: [] },
      {
        key: 'methodology.intro1',
        label: 'Introduction (paragraph 1)',
        kind: 'body',
        placeholder:
          'The methodology section outlines the approach and processes used to measure, calculate, and report our GHG emissions for the {{year}} {{calendarYear}}.',
        tokens: ['year', 'calendarYear'],
      },
      {
        key: 'methodology.intro2',
        label: 'Introduction (paragraph 2)',
        kind: 'body',
        placeholder:
          'This methodology adheres to internationally recognised standards, ensuring accuracy, consistency, and transparency in our emissions reporting. This report follows the GHG Protocol Corporate Standard, the most widely used international accounting tool for government and business leaders to understand, quantify, and manage GHG emissions.',
        tokens: [],
      },
      { key: 'methodology.scope1Title', label: 'Scope 1 card title', kind: 'heading', placeholder: 'Direct Emissions', tokens: [] },
      {
        key: 'methodology.scope1Body',
        label: 'Scope 1 card text',
        kind: 'body',
        placeholder:
          "Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your {{organisation}}'s activities.",
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
      { key: 'methodology.scope2Title', label: 'Scope 2 card title', kind: 'heading', placeholder: 'Indirect Emissions - Owned', tokens: [] },
      {
        key: 'methodology.scope2Body',
        label: 'Scope 2 card text',
        kind: 'body',
        placeholder:
          'Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your {{organisation}}. Although these emissions occur off-site, they are a result of your {{organisation}} energy use.',
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
      { key: 'methodology.scope3Title', label: 'Scope 3 card title', kind: 'heading', placeholder: 'Indirect Emissions - Not Owned', tokens: [] },
      {
        key: 'methodology.scope3Body',
        label: 'Scope 3 card text',
        kind: 'body',
        placeholder:
          "Indirect emissions that occur throughout the lifecycle of your {{organisation}}'s activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal. These emissions are often the largest part of an {{organisation}}'s carbon footprint but are not directly controlled by it.",
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
    ],
  },
  {
    sectionKey: 'emissionDetails',
    label: 'Emissions',
    blocks: [
      { key: 'emissions.heading', label: 'Heading', kind: 'heading', placeholder: 'Emissions', tokens: [] },
      {
        key: 'emissions.intro',
        label: 'Introduction',
        kind: 'body',
        placeholder:
          "This report provides a summary of {{company}} GHG emissions evaluation. The findings are based on the data collected, with consideration for its accuracy, categorisation, and validation. This assessment serves as a tool to highlight key areas where improvements can be made to reduce the company's environmental impact.",
        tokens: ['company'],
      },
      { key: 'emissions.overviewHeading', label: 'Overview heading', kind: 'heading', placeholder: 'Overview', tokens: [] },
      {
        key: 'emissions.overviewSummary',
        label: 'Overview summary sentence',
        kind: 'dataSentence',
        placeholder:
          'In {{calendarYear}}, {{company}} reported a total of {{total}} tCO2e emissions, comprising {{scope1}} tCO2e from Scope 1, {{scope2}} tCO2e from Scope 2 and {{scope3}} tCO2e from Scope 3.',
        tokens: ['calendarYear', 'company', 'total', 'scope1', 'scope2', 'scope3', 'marketTotal', 'marketScope1', 'marketScope2', 'marketScope3'],
      },
      { key: 'emissions.groupsHeading', label: 'Groups & Categories heading', kind: 'heading', placeholder: 'Groups and Categories', tokens: [] },
      {
        key: 'emissions.groupCommentary',
        label: 'Groups data sentence',
        kind: 'dataSentence',
        placeholder: 'The group that contributed the most emissions was {{topLabel}} with {{topValue}} tCO2e, followed by …',
        tokens: ['topLabel', 'topValue'],
      },
      {
        key: 'emissions.categoryCommentary',
        label: 'Categories data sentence',
        kind: 'dataSentence',
        placeholder: 'The category that contributed the most emissions was {{topLabel}} with {{topValue}} tCO2e, followed by …',
        tokens: ['topLabel', 'topValue'],
      },
      { key: 'emissions.countriesHeading', label: 'Countries & Locations heading', kind: 'heading', placeholder: 'Countries and {{locations}}', tokens: ['locations'] },
      {
        key: 'emissions.countryCommentary',
        label: 'Countries data sentence',
        kind: 'dataSentence',
        placeholder: 'The country that contributed the most emissions was {{topLabel}} with {{topValue}} tCO2e, followed by …',
        tokens: ['topLabel', 'topValue'],
      },
      { key: 'emissions.suppliersHeading', label: 'Suppliers & Products heading', kind: 'heading', placeholder: '{{suppliers}} and {{products}}', tokens: ['suppliers', 'products'] },
      {
        key: 'emissions.supplierCommentary',
        label: 'Suppliers data sentence',
        kind: 'dataSentence',
        placeholder: 'The {{supplier}} that contributed the most emissions was {{topLabel}} with {{topValue}} tCO2e, followed by …',
        tokens: ['supplier', 'topLabel', 'topValue'],
      },
      {
        key: 'emissions.productCommentary',
        label: 'Products data sentence',
        kind: 'dataSentence',
        placeholder: 'The {{product}} that contributed the most emissions was {{topLabel}} with {{topValue}} tCO2e, followed by …',
        tokens: ['product', 'topLabel', 'topValue'],
      },
    ],
  },
  {
    sectionKey: 'reductionDetails',
    label: 'Reduction Targets',
    blocks: [
      { key: 'reduction.heading', label: 'Heading', kind: 'heading', placeholder: 'Reduction Targets', tokens: [] },
      {
        key: 'reduction.body',
        label: 'Introduction',
        kind: 'body',
        placeholder:
          "This report provides an overview of {{company}}'s reduction targets. The bar chart illustrates the company's emissions for each year, while the accompanying line represents the established targets for reducing these emissions over time. This visual representation highlights the progress towards achieving the set reduction goals and serves as a benchmark for future environmental efforts.",
        tokens: ['company'],
      },
      {
        key: 'reduction.targetsSummary',
        label: 'Targets summary sentence',
        kind: 'dataSentence',
        placeholder:
          'The reduction targets are aligned with the Science Based Targets initiative (SBTi) / custom made by {{company}}, and span {{years}} years starting from the base year {{baseYear}} and aiming for completion in {{targetYear}}.',
        tokens: ['company', 'baseYear', 'targetYear', 'years', 'annualReductionPercentage', 'scope1Percentage', 'scope2LocationPercentage', 'scope2MarketPercentage', 'scope3Percentage'],
      },
    ],
  },
  {
    sectionKey: 'offsets',
    label: 'Offsets',
    blocks: [
      { key: 'offsets.heading', label: 'Heading', kind: 'heading', placeholder: 'Offsets', tokens: [] },
      {
        key: 'offsets.body',
        label: 'Introduction',
        kind: 'body',
        placeholder:
          "The following section outlines {{company}}'s carbon offsets. These offsets reflect actions taken beyond direct emission reductions, representing investments in projects that reduce or remove greenhouse gases from the atmosphere and help balance the company's overall emissions.",
        tokens: ['company'],
      },
      {
        key: 'offsets.commentary',
        label: 'Offsets data sentence',
        kind: 'dataSentence',
        placeholder: 'The total amount of emissions reduced through offsets is {{total}} tCO2e. The largest contribution came from {{topLabel}}, accounting for {{topValue}} tCO2e …',
        tokens: ['total', 'topLabel', 'topValue'],
      },
    ],
  },
  {
    sectionKey: null,
    label: 'Net Emissions',
    note: 'Shown together with the Offsets section (uses the Offsets toggle).',
    blocks: [
      { key: 'netEmissions.heading', label: 'Heading', kind: 'heading', placeholder: 'Net Emissions', tokens: [] },
      {
        key: 'netEmissions.body',
        label: 'Introduction',
        kind: 'body',
        placeholder:
          "Net emissions represent {{company}}'s total emissions after considering both the direct reductions achieved within its operations and the additional reductions secured through offset projects. This figure provides a more complete picture of the company's overall climate impact by balancing actual emissions with the measures taken to mitigate them.",
        tokens: ['company'],
      },
    ],
  },
  {
    sectionKey: 'emissionsOverTime',
    label: 'Emissions Over Time',
    blocks: [
      { key: 'emissionsOverTime.heading', label: 'Heading', kind: 'heading', placeholder: 'Emissions Over Time', tokens: [] },
      {
        key: 'emissionsOverTime.body',
        label: 'Introduction',
        kind: 'body',
        placeholder:
          "This chart compares {{company}}'s total emissions with net emissions over time, helping show how operational changes and offsets have influenced the overall emissions trajectory across reporting periods.",
        tokens: ['company'],
      },
    ],
  },
  {
    sectionKey: 'closing',
    label: 'Closing page',
    note: 'Shown on the final page, above the report logo. On direct (non-partner) reports a short system-generated credit line also appears beneath your message.',
    blocks: [
      { key: 'closing.heading', label: 'Heading (optional)', kind: 'heading', placeholder: 'e.g. Thank you', tokens: ['company'] },
      { key: 'closing.message', label: 'Message (optional)', kind: 'body', placeholder: 'e.g. For questions about this report, contact sustainability@company.com', tokens: ['company', 'year', 'calendarYear'] },
    ],
  },
]

// ---------------------------------------------------------------------------
// Project Report
// ---------------------------------------------------------------------------

export const PROJECT_REPORT_CONTENT: ReportContentArea[] = [
  {
    sectionKey: null,
    label: 'Cover page',
    note: 'The cover image and dates are set on the Design tab. Set the cover title and the small eyebrow label here.',
    blocks: [
      { key: 'cover.title', label: 'Cover page title', kind: 'heading', placeholder: 'Defaults to the project name', tokens: [], settingsField: 'customTitle' },
      { key: 'cover.eyebrow', label: 'Eyebrow label', kind: 'heading', placeholder: '{{projectLabel}} Report', tokens: ['projectLabel'] },
    ],
  },
  {
    sectionKey: 'overview',
    label: 'Overview',
    blocks: [
      { key: 'overview.heading', label: 'Heading', kind: 'heading', placeholder: '{{projectLabel}} Overview', tokens: ['projectLabel'] },
      { key: 'overview.totalsHeading', label: 'Totals sub-heading', kind: 'heading', placeholder: 'Total Emissions', tokens: [] },
    ],
  },
  {
    sectionKey: 'emissionsByScope',
    label: 'Emissions by Scope',
    blocks: [
      { key: 'emissionsByScope.heading', label: 'Heading', kind: 'heading', placeholder: 'Emissions by Scope', tokens: [] },
      {
        key: 'emissionsByScope.body',
        label: 'Introduction',
        kind: 'body',
        placeholder: "Breakdown of this {{projectLabel}}'s emissions across Scope 1 (direct), Scope 2 (purchased energy) and Scope 3 (value chain).",
        tokens: ['projectLabel'],
      },
    ],
  },
  {
    sectionKey: 'emissionsByCategory',
    label: 'Emissions by Category',
    blocks: [
      { key: 'emissionsByCategory.heading', label: 'Heading', kind: 'heading', placeholder: 'Emissions by Category', tokens: [] },
      {
        key: 'emissionsByCategory.body',
        label: 'Introduction',
        kind: 'body',
        placeholder: "Breakdown of this {{projectLabel}}'s total emissions across each emission category.",
        tokens: ['projectLabel'],
      },
    ],
  },
  {
    sectionKey: 'emissionsOverTime',
    label: 'Emissions Over Time',
    blocks: [
      { key: 'projectEmissionsOverTime.heading', label: 'Heading', kind: 'heading', placeholder: 'Emissions Over Time', tokens: [] },
      {
        key: 'projectEmissionsOverTime.body',
        label: 'Introduction',
        kind: 'body',
        placeholder: 'Monthly emissions for this {{projectLabel}} across its full duration.',
        tokens: ['projectLabel'],
      },
    ],
  },
  {
    sectionKey: 'methodology',
    label: 'Methodology',
    blocks: [
      { key: 'projectMethodology.heading', label: 'Heading', kind: 'heading', placeholder: 'Methodology', tokens: [] },
      {
        key: 'projectMethodology.intro1',
        label: 'Introduction (paragraph 1)',
        kind: 'body',
        placeholder:
          'This report follows the GHG Protocol Corporate Standard, the most widely used international accounting tool for government and business leaders to understand, quantify, and manage greenhouse gas emissions.',
        tokens: [],
      },
      {
        key: 'projectMethodology.intro2',
        label: 'Introduction (paragraph 2)',
        kind: 'body',
        placeholder:
          'All emissions are reported in tonnes of CO2 equivalent (tCO2e). Location-based Scope 2 figures use grid-average emission factors; market-based figures use supplier-specific factors where available.',
        tokens: [],
      },
      { key: 'projectMethodology.scope1Title', label: 'Scope 1 card title', kind: 'heading', placeholder: 'Direct Emissions', tokens: [] },
      {
        key: 'projectMethodology.scope1Body',
        label: 'Scope 1 card text',
        kind: 'body',
        placeholder:
          "Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your {{organisation}}'s activities.",
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
      { key: 'projectMethodology.scope2Title', label: 'Scope 2 card title', kind: 'heading', placeholder: 'Indirect Emissions - Owned', tokens: [] },
      {
        key: 'projectMethodology.scope2Body',
        label: 'Scope 2 card text',
        kind: 'body',
        placeholder:
          "Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your {{organisation}}. Although these emissions occur off-site, they are a result of your {{organisation}}'s energy use.",
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
      { key: 'projectMethodology.scope3Title', label: 'Scope 3 card title', kind: 'heading', placeholder: 'Indirect Emissions - Not Owned', tokens: [] },
      {
        key: 'projectMethodology.scope3Body',
        label: 'Scope 3 card text',
        kind: 'body',
        placeholder:
          "Indirect emissions that occur throughout the lifecycle of your {{organisation}}'s activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal.",
        tokens: METHODOLOGY_SCOPE_TOKENS,
      },
    ],
  },
  {
    sectionKey: 'closing',
    label: 'Closing page',
    note: 'Shown on the final page, above the report logo. On direct (non-partner) reports a short system-generated credit line also appears beneath your message.',
    blocks: [
      { key: 'closing.heading', label: 'Heading (optional)', kind: 'heading', placeholder: 'e.g. Thank you', tokens: ['company'] },
      { key: 'closing.message', label: 'Message (optional)', kind: 'body', placeholder: 'e.g. For questions about this report, contact sustainability@company.com', tokens: ['company'] },
    ],
  },
]

/** Flatten to the set of known override keys (used for backend-side validation mirrors and tests). */
export const carbonContentKeys = (): string[] =>
  CARBON_SUMMARY_CONTENT.flatMap(a => a.blocks.map(b => b.key))
export const projectContentKeys = (): string[] =>
  PROJECT_REPORT_CONTENT.flatMap(a => a.blocks.map(b => b.key))
